/**
 * Plantillas propias (Ajustes): nombre, descripción y secciones con una
 * instrucción cada una. Se eligen desde «+ Otra plantilla» en cada reunión.
 */
import { useState } from 'react'
import type { CustomTemplate, CustomTemplateInput } from '@eco/shared'
import { useAsync } from '../lib/hooks'
import { errorText, useApp, useToast } from './AppContext'
import { Modal } from './ui'

const MAX_SECCIONES = 8

const VACIA: CustomTemplateInput = {
  nombre: '',
  descripcion: '',
  secciones: [{ titulo: '', instruccion: '' }]
}

const EJEMPLO: CustomTemplateInput = {
  nombre: 'Reunión de ventas',
  descripcion: 'Llamadas con clientes potenciales',
  secciones: [
    { titulo: 'Dolor del cliente', instruccion: 'Problemas que menciona y su impacto' },
    { titulo: 'Presupuesto y plazos', instruccion: 'Montos, fechas y quién decide' },
    { titulo: 'Objeciones', instruccion: 'Dudas o frenos que planteó' },
    { titulo: 'Próximo paso acordado', instruccion: '' }
  ]
}

export function CustomTemplatesSection() {
  const { api } = useApp()
  const toast = useToast()
  const list = useAsync(() => api.customTemplates(), [api])
  const [editing, setEditing] = useState<{ id: string | null; draft: CustomTemplateInput } | null>(
    null
  )
  const [saving, setSaving] = useState(false)
  const [confirm, setConfirm] = useState<CustomTemplate | null>(null)

  async function save() {
    if (!editing) return
    const draft = {
      ...editing.draft,
      secciones: editing.draft.secciones.filter((s) => s.titulo.trim())
    }
    if (!draft.nombre.trim() || !draft.secciones.length) {
      toast('Ponle un nombre y al menos una sección.', 'error')
      return
    }
    setSaving(true)
    try {
      if (editing.id) await api.updateCustomTemplate(editing.id, draft)
      else await api.createCustomTemplate(draft)
      setEditing(null)
      toast('Plantilla guardada')
      await list.reload()
    } catch (e) {
      toast(errorText(e), 'error')
    } finally {
      setSaving(false)
    }
  }

  async function remove(t: CustomTemplate) {
    try {
      await api.deleteCustomTemplate(t.id)
      toast('Plantilla borrada')
      await list.reload()
    } catch (e) {
      toast(errorText(e), 'error')
    } finally {
      setConfirm(null)
    }
  }

  const setDraft = (f: (d: CustomTemplateInput) => CustomTemplateInput) =>
    setEditing((e) => (e ? { ...e, draft: f(e.draft) } : e))

  return (
    <section className="card stack">
      <h2>Plantillas propias</h2>
      <p className="faint" style={{ fontSize: 12.5, marginTop: -6 }}>
        Define las secciones que quieres en tus notas. Se eligen en cada reunión con «+ Otra
        plantilla»; las notas que ya existen no cambian si después editas la plantilla.
      </p>
      {list.data?.length ? (
        <ul className="stack" style={{ gap: 6, margin: 0, padding: 0, listStyle: 'none' }}>
          {list.data.map((t) => (
            <li key={t.id} className="row wrap">
              <span className="grow">
                <strong>{t.nombre}</strong>{' '}
                <span className="faint" style={{ fontSize: 12.5 }}>
                  · {t.secciones.map((s) => s.titulo).join(', ')}
                </span>
              </span>
              <button
                className="btn sm ghost"
                onClick={() =>
                  setEditing({
                    id: t.id,
                    draft: { nombre: t.nombre, descripcion: t.descripcion, secciones: t.secciones }
                  })
                }
              >
                Editar
              </button>
              <button className="btn sm ghost danger" onClick={() => setConfirm(t)}>
                Borrar
              </button>
            </li>
          ))}
        </ul>
      ) : (
        list.data && (
          <p className="muted" style={{ margin: 0 }}>
            Aún no tienes plantillas propias.
          </p>
        )
      )}
      <div className="row wrap">
        <button className="btn" onClick={() => setEditing({ id: null, draft: VACIA })}>
          Nueva plantilla
        </button>
        {!list.data?.length && (
          <button className="btn ghost" onClick={() => setEditing({ id: null, draft: EJEMPLO })}>
            Partir de un ejemplo
          </button>
        )}
      </div>

      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.id ? 'Editar plantilla' : 'Nueva plantilla'}
        footer={
          <>
            <button className="btn ghost" onClick={() => setEditing(null)}>
              Cancelar
            </button>
            <button className="btn primary" disabled={saving} onClick={() => void save()}>
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
          </>
        }
      >
        {editing && (
          <div className="stack">
            <label className="field">
              <span>Nombre</span>
              <input
                value={editing.draft.nombre}
                maxLength={60}
                onChange={(e) => setDraft((d) => ({ ...d, nombre: e.target.value }))}
              />
            </label>
            <label className="field">
              <span>Descripción (opcional)</span>
              <input
                value={editing.draft.descripcion}
                maxLength={200}
                onChange={(e) => setDraft((d) => ({ ...d, descripcion: e.target.value }))}
              />
            </label>
            <fieldset className="stack" style={{ border: 0, padding: 0, margin: 0, gap: 10 }}>
              <legend style={{ fontSize: 13, marginBottom: 6 }}>
                Secciones (además del resumen, los puntos clave y las tareas)
              </legend>
              {editing.draft.secciones.map((s, i) => (
                <div key={i} className="stack" style={{ gap: 4 }}>
                  <div className="row">
                    <input
                      className="grow"
                      aria-label={`Título de la sección ${i + 1}`}
                      placeholder="Título, p. ej. «Objeciones»"
                      value={s.titulo}
                      maxLength={60}
                      onChange={(e) =>
                        setDraft((d) => ({
                          ...d,
                          secciones: d.secciones.map((x, j) =>
                            j === i ? { ...x, titulo: e.target.value } : x
                          )
                        }))
                      }
                    />
                    <button
                      className="btn sm ghost"
                      aria-label={`Quitar la sección ${i + 1}`}
                      disabled={editing.draft.secciones.length === 1}
                      onClick={() =>
                        setDraft((d) => ({
                          ...d,
                          secciones: d.secciones.filter((_, j) => j !== i)
                        }))
                      }
                    >
                      Quitar
                    </button>
                  </div>
                  <input
                    aria-label={`Qué va en la sección ${i + 1}`}
                    placeholder="Qué debe ir aquí (opcional)"
                    value={s.instruccion}
                    maxLength={300}
                    onChange={(e) =>
                      setDraft((d) => ({
                        ...d,
                        secciones: d.secciones.map((x, j) =>
                          j === i ? { ...x, instruccion: e.target.value } : x
                        )
                      }))
                    }
                  />
                </div>
              ))}
              {editing.draft.secciones.length < MAX_SECCIONES && (
                <button
                  className="btn sm ghost"
                  style={{ alignSelf: 'flex-start' }}
                  onClick={() =>
                    setDraft((d) => ({
                      ...d,
                      secciones: [...d.secciones, { titulo: '', instruccion: '' }]
                    }))
                  }
                >
                  + Agregar sección
                </button>
              )}
            </fieldset>
          </div>
        )}
      </Modal>

      <Modal
        open={!!confirm}
        onClose={() => setConfirm(null)}
        title="¿Borrar la plantilla?"
        footer={
          <>
            <button className="btn ghost" onClick={() => setConfirm(null)}>
              Cancelar
            </button>
            <button className="btn danger" onClick={() => confirm && void remove(confirm)}>
              Borrar
            </button>
          </>
        }
      >
        <p>
          Las notas que ya se generaron con «{confirm?.nombre}» se mantienen; solo deja de estar
          disponible para notas nuevas.
        </p>
      </Modal>
    </section>
  )
}
